// Files as a tab (spec "Files"): the cloud machine's home, folders first, with breadcrumbs and,
// at home, Recent on top (what an agent just made). A file opens in the preview beside the list
// (text and code, images, PDFs, CSVs) or downloads; files dropped on the pane, or picked with
// Upload, go into the folder shown. ⋯ on a row: Download, Rename, Copy path, Open in terminal,
// Delete (to the machine's Trash, asked first). Changes on the machine show as they happen.

import {
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconDots,
  IconDownload,
  IconEye,
  IconEyeOff,
  IconFolder,
  IconFileUpload,
  IconFolderPlus,
  IconFolderUp,
  IconPlus,
  IconPencil,
  IconSearch,
  IconTerminal2,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import * as fs from '../../net/files';
import type { FileItem } from '../../net/files';
import { loadPreview, previewKind, type Preview } from '../../net/filePreview';
import { fmtBytes } from '../../pages/MachineControls';
import { toast, useStore } from '../../store';
import { Dialog } from '../Dialog';
import { Button, Empty, Ic, Meta, Mono, Pix, Tool, Tooltip, presence, type TablerIcon } from '../ds';
import { menuKeys, useMenu } from '../menu';
import { copyText } from '../sheets';
import { agoPhrase, useNow } from '../time';
import { useActions } from '../Workspace';
import { FileMark } from './FileMark';
import './files.css';

const secs = (mtime: number) => mtime / 1e9;

/** When it changed: "5m ago" today, else the day ("Oct 3", "Mar 2025" before this year). */
function modified(mtime: number, now: number) {
  const s = now / 1000 - secs(mtime);
  if (s < 86400) return agoPhrase(s);
  const d = new Date(mtime / 1e6);
  return d.toLocaleDateString(undefined, d.getFullYear() === new Date(now).getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', year: 'numeric' });
}

/** The folder a Recent file is in, as a path from home ("~" at home). */
const folderOf = (path = '') => (path.includes('/') ? `~/${path.slice(0, path.lastIndexOf('/'))}` : '~');
/** The folders every one of these starts with ("~/projects/cmx"), as parts. */
function sharedFolder(folders: string[]) {
  if (folders.length < 2) return [];
  let common = folders[0].split('/');
  for (const f of folders.slice(1)) {
    const parts = f.split('/');
    let n = 0;
    while (n < common.length && n < parts.length && common[n] === parts[n]) n++;
    common = common.slice(0, n);
  }
  return common;
}
/** A folder as Recent's Location says it: less the shared start ("web/src/ui"), the shared folder's own
 *  name when that's all it is, "Home" at home. As the Mac's. */
function shortFolder(folder: string, shared: string[]) {
  if (folder === '~') return 'Home';
  const parts = folder.split('/');
  const rest = parts.slice(shared.length);
  return rest.length ? rest.join('/') : (parts[parts.length - 1] ?? '~');
}

const LAST = 'cmx.files.folder';
const HIDDEN = 'cmx.files.hidden';
const RECENT_OPEN = 'cmx.files.recentOpen';
/** How many Recent rows there were last time: the still rows while it loads (no shimmer, as on the Mac). */
const RECENT_COUNT = 'cmx.files.recentCount';
const stored = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const store = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* a convenience */
  }
};
const isHidden = (it: FileItem) => it.name.startsWith('.');

// Folders search doesn't go into: what tools fill (and macOS's own hiding place).
const UNWALKED = new Set(['node_modules', '.git', 'Library', '__pycache__', '.venv', 'venv', 'target', 'dist', 'build']);
const walkable = (it: FileItem) => it.kind === 'dir' && !isHidden(it) && !UNWALKED.has(it.name);

/** Everything under a folder, for search: eight folders at a time, level by level, dotfolders and tool
 *  output left out, up to 600 folders or 20,000 items. Each item gets its path under home, as Recent's
 *  have; `onLevel` hears what's found so far. Stops when `live()` says the folder isn't shown any more. */
async function walk(start: FileItem[], base: string, live: () => boolean, onLevel: (found: FileItem[], capped: boolean) => void) {
  const found: FileItem[] = [];
  let level = start.filter(walkable).map((f) => ({ id: f.id, rel: f.name }));
  let folders = 0;
  while (level.length) {
    if (folders + level.length > 600 || found.length > 20_000) return onLevel(found, true);
    folders += level.length;
    const next: typeof level = [];
    let i = 0;
    const worker = async () => {
      while (i < level.length) {
        const f = level[i++];
        const kids = await fs.list(f.id).catch(() => [] as FileItem[]);
        for (const k of kids) {
          const rel = `${f.rel}/${k.name}`;
          found.push({ ...k, path: base ? `${base}/${rel}` : rel });
          if (walkable(k)) next.push({ id: k.id, rel });
        }
      }
    };
    await Promise.all(Array.from({ length: 8 }, worker));
    if (!live()) return;
    onLevel([...found], false);
    level = next;
  }
}

/** A name with what search typed lit (the first place it's found). */
function Hit({ name, q }: { name: string; q: string }) {
  const i = q ? name.toLocaleLowerCase().indexOf(q.toLocaleLowerCase()) : -1;
  if (i < 0) return <>{name}</>;
  return (
    <>
      {name.slice(0, i)}
      <mark className="fl-hit">{name.slice(i, i + q.length)}</mark>
      {name.slice(i + q.length)}
    </>
  );
}

type Upload = { key: number; name: string; progress: number };
type MenuItem = { key: string; label: string; icon: TablerIcon; danger?: boolean; line?: boolean; testid: string; onSelect: () => void };
/** A file picked or dropped with the folder it's in, as a path under what was picked ("photos/2024/a.jpg"). */
type Picked = { rel: string; file: File };
type Ask = { kind: 'mkdir' } | { kind: 'rename'; item: FileItem } | { kind: 'trash'; item: FileItem };

let uploadKey = 0;

/** Everything in a dropped folder, read through the entries API (in batches, as readEntries gives them). */
async function readEntry(e: FileSystemEntry, base: string, out: Picked[]) {
  const rel = base ? `${base}/${e.name}` : e.name;
  if (e.isFile) {
    const file = await new Promise<File>((ok, bad) => (e as FileSystemFileEntry).file(ok, bad));
    out.push({ rel, file });
  } else if (e.isDirectory) {
    const reader = (e as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((ok, bad) => reader.readEntries(ok, bad));
      if (!batch.length) break;
      for (const k of batch) await readEntry(k, rel, out);
    }
  }
}

export function FilesView() {
  const a = useActions();
  const machine = useStore((s) => s.me?.machine);
  const now = useNow(30_000);
  const [folder, setFolder] = useState(() => stored(LAST) ?? fs.HOME);
  // dotfiles hidden, as Finder hides them, until ⇧⌘. (or the eye) shows them
  const [hidden, setHidden] = useState(() => stored(HIDDEN) === '1');
  const [crumbs, setCrumbs] = useState<FileItem[]>([]);
  const [items, setItems] = useState<FileItem[] | null>(null);
  const [recent, setRecent] = useState<FileItem[]>([]);
  const [recentKnown, setRecentKnown] = useState(false);
  const [recentOpen, setRecentOpen] = useState(() => stored(RECENT_OPEN) !== '0');
  const [error, setError] = useState('');
  const [open, setOpen] = useState<FileItem | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [over, setOver] = useState(false);
  // search: this folder's items by name, and (walked once per folder, on the first letter) the folders inside it
  const [query, setQuery] = useState('');
  const [inside, setInside] = useState<{ folder: string; items: FileItem[]; walking: boolean; capped: boolean } | null>(null);
  const searchField = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const folderPicker = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const folderRef = useRef(folder);
  folderRef.current = folder;
  const home = folder === fs.HOME;
  const here = fs.tildePath(crumbs);

  const load = useCallback(async (id: string, quiet = false) => {
    if (!quiet) setItems(null);
    // Recent at once, beside the folder (not after it), so it's there with the list
    if (id === fs.HOME)
      void fs.recent().then(
        (r) => {
          if (folderRef.current !== fs.HOME) return;
          setRecent(r);
          setRecentKnown(true);
          store(RECENT_COUNT, String(Math.min(r.length, 8)));
        },
        () => setRecentKnown(true),
      );
    try {
      const [its, path] = await Promise.all([fs.list(id), fs.path(id)]);
      if (folderRef.current !== id) return;
      setItems(its);
      setCrumbs(path);
      setError('');
    } catch (e) {
      if (folderRef.current !== id) return;
      // a folder that's gone (or from before a restart that lost it): back home
      if (id !== fs.HOME && /not found|no such/i.test(String(e))) return go(fs.HOME);
      setError(e instanceof Error ? e.message : String(e));
      setItems([]);
    }
  }, []);

  const go = useCallback(
    (id: string) => {
      folderRef.current = id;
      setFolder(id);
      setOpen(null);
      setQuery('');
      setInside(null);
      store(LAST, id);
      void load(id);
    },
    [load],
  );

  useEffect(() => void load(folderRef.current), [load]);

  // what changes on the machine shows here: the folder shown again (quietly) when anything in it moved
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    const stop = new AbortController();
    let t: ReturnType<typeof setTimeout> | undefined;
    void fs.follow((c) => {
      const f = folderRef.current;
      const shown = new Set((itemsRef.current ?? []).map((it) => it.id));
      const touches = c.expired || (c.items ?? []).some((it) => it.parent === f || shown.has(it.id)) || (c.gone ?? []).some((id) => shown.has(id) || id === f);
      if (!touches && f !== fs.HOME) return;
      clearTimeout(t);
      t = setTimeout(() => void load(folderRef.current, true), 150);
    }, stop.signal);
    return () => {
      stop.abort();
      clearTimeout(t);
    };
  }, [load]);

  const flash = (id: string) => {
    setFresh((s) => new Set(s).add(id));
    setTimeout(
      () =>
        setFresh((s) => {
          const n = new Set(s);
          n.delete(id);
          return n;
        }),
      1600,
    );
  };

  /** One file into folder `into`, its progress in the uploads list; the row shows (and flashes) when that
   *  folder is the one on screen. Returns whether it went. */
  const sendOne = async (into: string, file: File, label = file.name) => {
    const key = ++uploadKey;
    setUploads((u) => [...u, { key, name: label, progress: 0 }]);
    try {
      const it = await fs.upload(into, file, (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))));
      if (folderRef.current === into) {
        setItems((its) => fs.sortItems([...(its ?? []).filter((x) => x.id !== it.id), it]));
        flash(it.id);
      }
      return true;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    } finally {
      setUploads((u) => u.filter((x) => x.key !== key));
    }
  };

  /** What didn't go: each by name, or one line when more than three (a folder's worth). */
  const failed = (bad: { name: string; why: string }[]) => {
    if (!bad.length) return;
    if (bad.length > 3) toast({ title: `Couldn’t upload ${bad.length} files`, body: bad[0].why, kind: 'error' });
    else for (const b of bad) toast({ title: `Couldn’t upload ${b.name}`, body: b.why, kind: 'error' });
  };

  const upload = async (files: File[]) => {
    const into = folderRef.current;
    const bad: { name: string; why: string }[] = [];
    await Promise.all(
      files.map(async (file) => {
        const r = await sendOne(into, file);
        if (r !== true) bad.push({ name: file.name, why: r });
      }),
    );
    failed(bad);
  };

  /** Folders (picked, or dropped): each made here with what's in it, folder by folder, then the files into
   *  them, four at a time. A name that's taken is the folder already there. */
  const uploadFolders = async (picked: Picked[]) => {
    const ids = new Map<string, string>([['', folderRef.current]]);
    const dirOf = async (rel: string): Promise<string> => {
      const hit = ids.get(rel);
      if (hit) return hit;
      const cut = rel.lastIndexOf('/');
      const parent = await dirOf(cut < 0 ? '' : rel.slice(0, cut));
      const name = rel.slice(cut + 1);
      let id: string;
      try {
        const it = await fs.mkdir(parent, name);
        id = it.id;
        if (parent === folderRef.current) {
          setItems((its) => fs.sortItems([...(its ?? []).filter((x) => x.id !== it.id), it]));
          flash(it.id);
        }
      } catch (e) {
        const there = (await fs.list(parent).catch(() => [])).find((x) => x.kind === 'dir' && x.name === name);
        if (!there) throw e;
        id = there.id;
      }
      ids.set(rel, id);
      return id;
    };
    const bad: { name: string; why: string }[] = [];
    let i = 0;
    const worker = async () => {
      while (i < picked.length) {
        const p = picked[i++];
        const cut = p.rel.lastIndexOf('/');
        try {
          const dir = await dirOf(cut < 0 ? '' : p.rel.slice(0, cut));
          const r = await sendOne(dir, p.file, p.rel);
          if (r !== true) bad.push({ name: p.rel, why: r });
        } catch (e) {
          bad.push({ name: p.rel, why: e instanceof Error ? e.message : String(e) });
        }
      }
    };
    // the folders first, one by one, so the four workers don't make the same one twice
    for (const p of picked) {
      const cut = p.rel.lastIndexOf('/');
      if (cut > 0) await dirOf(p.rel.slice(0, cut)).catch(() => {});
    }
    await Promise.all(Array.from({ length: 4 }, worker));
    failed(bad);
  };

  const fail = (what: string) => (e: unknown) => toast({ title: what, body: e instanceof Error ? e.message : String(e), kind: 'error' });

  const pathOf = (it: FileItem) => (it.path ? `~/${it.path}` : fs.tildePath(crumbs, it.name));

  const menu = (it: FileItem): MenuItem[] => [
    ...(it.kind === 'dir' ? [] : [{ key: 'dl', label: 'Download', icon: IconDownload, testid: 'files-download', onSelect: () => fs.download(it) }]),
    { key: 'rename', label: 'Rename…', icon: IconPencil, testid: 'files-rename', onSelect: () => setAsk({ kind: 'rename', item: it }) },
    { key: 'copy', label: 'Copy path', icon: IconCopy, testid: 'files-copy-path', onSelect: () => void copyText(pathOf(it)).then((ok) => ok && toast({ title: 'Path copied', body: pathOf(it) }, 2500)) },
    {
      key: 'term',
      label: 'Open in terminal',
      icon: IconTerminal2,
      testid: 'files-terminal',
      onSelect: () => void a.newSession({ cwd: it.kind === 'dir' ? pathOf(it) : it.path ? folderOf(it.path) : here }),
    },
    { key: 'trash', label: 'Delete…', icon: IconTrash, danger: true, testid: 'files-delete', onSelect: () => setAsk({ kind: 'trash', item: it }) },
  ];

  const toggleHidden = () =>
    setHidden((h) => {
      store(HIDDEN, h ? '0' : '1');
      return !h;
    });
  const shown = useMemo(() => (hidden ? items : items?.filter((it) => !isHidden(it))), [items, hidden]);
  const hiddenCount = (items?.length ?? 0) - (shown?.length ?? 0);

  const activate = (it: FileItem) => (it.kind === 'dir' ? go(it.id) : setOpen(it));

  const toggleRecent = () =>
    setRecentOpen((o) => {
      store(RECENT_OPEN, o ? '0' : '1');
      return !o;
    });
  /** A Recent row's ⋯: Show in folder first; no Rename or Delete (they belong to the folder's own list). */
  const recentMenu = (it: FileItem): MenuItem[] => [
    { key: 'folder', label: 'Show in folder', icon: IconFolder, testid: 'files-show-folder', onSelect: () => go(it.parent) },
    ...menu(it).filter((m) => m.key !== 'rename' && m.key !== 'trash'),
  ];

  const needle = query.trim();
  const searching = needle !== '';
  // Recent at home while it loads, then while it has something the home list doesn't already show
  // (a small home whose Recent only repeats its own files leaves it out)
  const homeIds = useMemo(() => new Set((items ?? []).map((it) => it.id)), [items]);
  const onlyHome = recent.length > 0 && recent.every((it) => homeIds.has(it.id));
  const showsRecent = home && !searching && (!recentKnown || (recent.length > 0 && !(onlyHome && (items?.length ?? 0) <= 12)));
  const recentShown = recent.slice(0, 8);
  const recentShared = sharedFolder(recentShown.map((it) => folderOf(it.path)));
  const placeholders = Math.max(1, Math.min(8, Number(stored(RECENT_COUNT) ?? '4') || 4));
  const matches = (it: FileItem) => it.name.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
  const visible = useMemo(() => (searching ? shown?.filter(matches) : shown), [shown, searching, needle]);
  // the first letter typed walks the folders inside this one, once
  const walkFolder = inside?.folder;
  useEffect(() => {
    if (!searching || !items || walkFolder === folder) return;
    const root = folder;
    const base = crumbs.slice(1).map((c) => c.name).join('/');
    setInside({ folder: root, items: [], walking: true, capped: false });
    void walk(items, base, () => folderRef.current === root, (found, capped) =>
      setInside((w) => (w?.folder === root ? { folder: root, items: found, walking: w.walking && !capped, capped } : w)),
    ).then(() => setInside((w) => (w?.folder === root ? { ...w, walking: false } : w)));
  }, [searching, folder, items, crumbs, walkFolder]);
  const deeper = useMemo(() => {
    if (!searching || inside?.folder !== folder) return [];
    return inside.items
      .filter((it) => (hidden || !isHidden(it)) && matches(it))
      .sort((x, y) => (x.kind === 'dir') !== (y.kind === 'dir') ? (x.kind === 'dir' ? -1 : 1) : x.name.localeCompare(y.name, undefined, { numeric: true }));
  }, [inside, folder, searching, needle, hidden]);
  const walking = searching && inside?.folder === folder && inside.walking;
  /** Where a deeper match is, under the folder shown ("old/2024"). */
  const insideFolder = (it: FileItem) => (it.path ?? '').split('/').slice(crumbs.length - 1, -1).join('/');
  const count = !shown?.length
    ? ''
    : !searching
      ? `${shown.length} item${shown.length === 1 ? '' : 's'}`
      : deeper.length === 0
        ? `${visible?.length ?? 0} of ${shown.length}`
        : visible?.length
          ? `${visible.length} here · ${deeper.length} inside`
          : `${deeper.length} inside`;

  // ↑ ↓ move along the rows, ⌫ (or ⌘↑) goes up a folder, Esc closes the preview
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    // a menu or a dialog has these keys first
    if (e.defaultPrevented || (e.target as Element).closest('.ws-menu, dialog')) return;
    const rows = Array.from(list.current?.querySelectorAll<HTMLButtonElement>('.fl-row-open') ?? []);
    const i = rows.indexOf(document.activeElement as HTMLButtonElement);
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.metaKey && i >= 0) {
      e.preventDefault();
      rows[Math.max(0, Math.min(rows.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    } else if ((e.key === 'Backspace' || (e.key === 'ArrowUp' && e.metaKey)) && crumbs.length > 1 && !(e.target instanceof HTMLInputElement)) {
      e.preventDefault();
      go(crumbs[crumbs.length - 2].id);
    } else if (e.code === 'Period' && e.shiftKey && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      toggleHidden();
    } else if (e.key === 'f' && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
      e.preventDefault();
      searchField.current?.focus();
      searchField.current?.select();
    } else if (e.key === 'Escape' && open) {
      setOpen(null);
    }
  };

  const drop = {
    onDragEnter: (e: DragEvent) => e.dataTransfer.types.includes('Files') && setOver(true),
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e: DragEvent) => !e.currentTarget.contains(e.relatedTarget as Node) && setOver(false),
    onDrop: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      setOver(false);
      const entries = Array.from(e.dataTransfer.items).map((x) => x.webkitGetAsEntry?.() ?? null);
      const files = Array.from(e.dataTransfer.files).filter((_, i) => !entries[i]?.isDirectory);
      const dirs = entries.filter((x): x is FileSystemEntry => !!x?.isDirectory);
      if (files.length) void upload(files);
      // a folder comes with everything in it
      if (dirs.length)
        void (async () => {
          const picked: Picked[] = [];
          for (const d of dirs) await readEntry(d, '', picked);
          await uploadFolders(picked);
        })();
    },
  };

  if (!machine) return null;

  return (
    <div className="ws-view ws-mview fl" data-testid="files-view" data-open={open ? '' : undefined} onKeyDown={keys} {...drop}>
      <div className="fl-main">
        {/* the Mac's head: where you are as the title (Files › Projects › app), then New and the folder's ⋯ */}
        <div className="ws-mview-head fl-head">
          <TitleCrumbs crumbs={crumbs} go={go} />
          <HeadMenu
            label="New"
            testid="files-new"
            trigger={
              <>
                <Ic icon={IconPlus} size={14} />
                New
              </>
            }
            items={[
              { key: 'mkdir', label: 'New folder', icon: IconFolderPlus, testid: 'files-mkdir', onSelect: () => setAsk({ kind: 'mkdir' }) },
              { key: 'upload', label: 'File upload', icon: IconFileUpload, line: true, testid: 'files-upload', onSelect: () => picker.current?.click() },
              { key: 'folder', label: 'Folder upload', icon: IconFolderUp, testid: 'files-upload-folder', onSelect: () => folderPicker.current?.click() },
            ]}
          />
          <HeadMenu
            label="Folder options"
            testid="files-more"
            square
            trigger={<Ic icon={IconDots} size={16} />}
            items={[
              {
                key: 'hidden',
                label: hidden ? 'Hide hidden files' : hiddenCount > 0 ? `Show ${hiddenCount} hidden file${hiddenCount === 1 ? '' : 's'}` : 'Show hidden files',
                icon: hidden ? IconEyeOff : IconEye,
                testid: 'files-hidden',
                onSelect: toggleHidden,
              },
              { key: 'copy', label: 'Copy path', icon: IconCopy, testid: 'files-copy-here', onSelect: () => void copyText(here).then((ok) => ok && toast({ title: 'Path copied', body: here }, 2500)) },
              { key: 'term', label: 'Open in terminal', icon: IconTerminal2, testid: 'files-terminal-here', onSelect: () => void a.newSession({ cwd: here }) },
            ]}
          />
          <input
            ref={folderPicker}
            type="file"
            hidden
            data-testid="files-folder-picker"
            // a folder and everything in it (each file's webkitRelativePath says where)
            {...({ webkitdirectory: '' } as Record<string, string>)}
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []).map((file) => ({ rel: file.webkitRelativePath || file.name, file }));
              e.target.value = '';
              if (picked.length) void uploadFolders(picked);
            }}
          />
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            data-testid="files-picker"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length) void upload(files);
            }}
          />
        </div>


        <div className="fl-toolbar">
          <label className="fl-search">
            <Ic icon={IconSearch} size={14} />
            <input
              ref={searchField}
              className="input"
              type="search"
              placeholder={`Search ${home ? 'home' : (crumbs[crumbs.length - 1]?.name ?? 'this folder')}`}
              aria-label="Search this folder"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape' || !query) return;
                e.preventDefault();
                e.stopPropagation();
                setQuery('');
              }}
              data-testid="files-search"
            />
          </label>
          {count && <Meta className="fl-count">{count}</Meta>}
        </div>

        {showsRecent && (
          <section className="fl-recent" aria-labelledby="fl-recent">
            <div className="ws-sec-head">
              {/* folds like Drive's: the chevron after the word, turned when folded; remembered */}
              <button type="button" className="fl-recent-toggle" aria-expanded={recentOpen} onClick={toggleRecent} data-testid="files-recent-toggle">
                <Pix size={11} className="ws-sec-pix">
                  <span id="fl-recent">Recent</span>
                </Pix>
                <Ic icon={IconChevronDown} size={12} />
              </button>
            </div>
            {recentOpen && (
              <div className="fl-table" role="table" aria-labelledby="fl-recent">
                <div className="fl-row" role="row" data-head data-inside>
                  {['Name', 'Last changed', 'Location'].map((t) => (
                    <span key={t} role="columnheader" className="fl-th">
                      {t}
                    </span>
                  ))}
                  <span role="columnheader" aria-label="Actions" />
                </div>
                {!recentKnown
                  ? Array.from({ length: placeholders }, (_, i) => (
                      <div key={i} className="fl-row" role="row" data-inside data-ph aria-hidden="true">
                        <span className="fl-name fl-ph-name">
                          <span className="fl-ph fl-ph-tile" />
                          <span className="fl-ph" style={{ width: `${[52, 38, 64, 44][i % 4]}%` }} />
                        </span>
                        <span className="fl-ph" style={{ width: 52 }} />
                        <span className="fl-ph" style={{ width: 72 }} />
                        <span />
                      </div>
                    ))
                  : recentShown.map((it, i) => (
                      <div key={it.id} className="fl-row" role="row" data-inside style={{ ['--i' as string]: i }} data-on={open?.id === it.id ? '' : undefined} data-testid="files-recent" data-name={it.name}>
                        <span role="cell" className="fl-name">
                          <button type="button" className="fl-row-open" onClick={() => activate(it)}>
                            <FileMark item={it} />
                            <span className="fl-name-n">{it.name}</span>
                          </button>
                        </span>
                        <span role="cell" className="fl-num" data-muted>
                          {modified(it.mtime, now)}
                        </span>
                        <span role="cell" className="fl-where">
                          <button type="button" className="fl-link" onClick={() => go(it.parent)} aria-label={`Show in ${folderOf(it.path)}`} title={folderOf(it.path)}>
                            <Ic icon={IconFolder} size={16} />
                            <span>
                              <bdi>{shortFolder(folderOf(it.path), recentShared)}</bdi>
                            </span>
                          </button>
                        </span>
                        <span role="cell" className="fl-more">
                          <FileMenu name={it.name} items={recentMenu(it)} testid="files-recent-menu" />
                        </span>
                      </div>
                    ))}
              </div>
            )}
          </section>
        )}

        <section className="fl-list" aria-label={here}>
          {showsRecent && (
            <div className="ws-sec-head">
              <span className="ws-sec-title">
                <Pix size={11} className="ws-sec-pix">
                  <span>Home</span>
                </Pix>
                <Mono>{shown?.length ?? ''}</Mono>
              </span>
            </div>
          )}
          {error ? (
            <Empty art="box-closed" size="md" title={`Can’t reach ${machine.id}’s files`} lead={error} data-testid="files-error">
              <Button size="sm" onClick={() => void load(folder)}>
                Try again
              </Button>
            </Empty>
          ) : shown && !shown.length ? (
            <Empty art="box" size="md" title={home ? 'Nothing here yet' : 'This folder is empty'} lead="Drop files here, or upload them from this computer." data-testid="files-empty">
              <Button size="sm" variant="primary" onClick={() => picker.current?.click()}>
                Upload files
              </Button>
              <Button size="sm" onClick={() => setAsk({ kind: 'mkdir' })}>
                New folder
              </Button>
            </Empty>
          ) : searching && visible && !visible.length && !deeper.length && !walking ? (
            <Empty
              art="search"
              size="md"
              title={`No matches for “${needle}”`}
              lead={home ? 'No file or folder in your home has that in its name.' : `Nothing in ${crumbs[crumbs.length - 1]?.name ?? 'this folder'} or the folders inside it has that in its name.`}
              data-testid="files-no-match"
            >
              <Button size="sm" onClick={() => setQuery('')}>
                Clear search
              </Button>
            </Empty>
          ) : searching && visible && !visible.length ? null : (
            <div className="fl-table" role="table" aria-label={here} ref={list}>
              <div className="fl-row" role="row" data-head>
                {['Name', 'Modified', 'Size'].map((t) => (
                  <span key={t} role="columnheader" className="fl-th">
                    {t}
                  </span>
                ))}
                <span role="columnheader" aria-label="Actions" />
              </div>
              {!items && <div className="fl-loading" aria-label="Loading" data-testid="files-loading" />}
              {visible?.map((it, i) => (
                <div
                  key={it.id}
                  className="fl-row"
                  role="row"
                  style={{ ['--i' as string]: Math.min(i, 14) }}
                  data-on={open?.id === it.id ? '' : undefined}
                  data-fresh={fresh.has(it.id) ? '' : undefined}
                  data-testid="files-row"
                  data-name={it.name}
                >
                  <span role="cell" className="fl-name">
                    <button type="button" className="fl-row-open" onClick={() => activate(it)}>
                      <FileMark item={it} />
                      <span className="fl-name-n">
                        <Hit name={it.name} q={needle} />
                      </span>
                      {it.kind === 'link' && it.target && <Meta>→ {it.target}</Meta>}
                    </button>
                  </span>
                  <span role="cell" className="fl-num" data-muted>
                    {modified(it.mtime, now)}
                  </span>
                  <span role="cell" className="fl-num" data-muted={it.kind === 'dir' ? '' : undefined}>
                    {it.kind === 'dir' ? '–' : fmtBytes(it.size)}
                  </span>
                  <span role="cell" className="fl-more">
                    <FileMenu name={it.name} items={menu(it)} testid="files-row-menu" />
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {searching && (deeper.length > 0 || walking) && (
          <section className="fl-inside" aria-labelledby="fl-inside" data-testid="files-inside">
            <div className="ws-sec-head">
              <span className="ws-sec-title">
                <Pix size={11} className="ws-sec-pix">
                  <span id="fl-inside">In folders inside</span>
                </Pix>
                {deeper.length > 0 && <Mono>{deeper.length}</Mono>}
              </span>
              {walking && <Meta className="fl-looking">looking…</Meta>}
            </div>
            {deeper.length > 0 && (
              <div className="fl-table" role="table" aria-label={`Matches in the folders inside ${here}`}>
                <div className="fl-row" role="row" data-head data-inside>
                  {['Name', 'Modified', 'Location'].map((t) => (
                    <span key={t} role="columnheader" className="fl-th">
                      {t}
                    </span>
                  ))}
                  <span role="columnheader" aria-label="Actions" />
                </div>
                {deeper.slice(0, 100).map((it, i) => (
                  <div key={it.id} className="fl-row" role="row" data-inside style={{ ['--i' as string]: Math.min(i, 14) }} data-on={open?.id === it.id ? '' : undefined} data-testid="files-inside-row" data-name={it.name}>
                    <span role="cell" className="fl-name">
                      <button type="button" className="fl-row-open" onClick={() => activate(it)}>
                        <FileMark item={it} />
                        <span className="fl-name-n">
                          <Hit name={it.name} q={needle} />
                        </span>
                      </button>
                    </span>
                    <span role="cell" className="fl-num" data-muted>
                      {modified(it.mtime, now)}
                    </span>
                    <span role="cell" className="fl-where">
                      <button type="button" className="fl-link" onClick={() => go(it.parent)} aria-label={`Show in ${insideFolder(it)}`}>
                        <Ic icon={IconFolder} size={16} />
                        <span>
                          <bdi>{insideFolder(it)}</bdi>
                        </span>
                      </button>
                    </span>
                    <span role="cell" className="fl-more">
                      <FileMenu name={it.name} items={menu(it).filter((m) => m.key !== 'rename' && m.key !== 'trash')} testid="files-inside-menu" />
                    </span>
                  </div>
                ))}
              </div>
            )}
            {deeper.length > 100 && <Meta className="fl-note">And {deeper.length - 100} more. Type more of the name to narrow them down.</Meta>}
            {inside?.capped && !walking && <Meta className="fl-note">Looked in the first 600 folders inside.</Meta>}
          </section>
        )}
      </div>

      {open && <PreviewPane key={open.id} item={open} now={now} menu={menu(open)} onClose={() => setOpen(null)} onFolder={open.path ? () => go(open.parent) : undefined} />}

      {over && (
        <div className="fl-drop" data-testid="files-drop" aria-hidden="true">
          <div className="fl-drop-card">
            <Ic icon={IconUpload} size={20} />
            <span>
              Drop to upload to <Mono size={12}>{here}</Mono>
            </span>
          </div>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="fl-uploads" role="status" data-testid="files-uploads">
          {uploads.map((u) => (
            <div key={u.key} className="fl-upload">
              <span className="fl-upload-name" title={u.name}>
                {u.name}
              </span>
              <Meta>{Math.round(u.progress * 100)}%</Meta>
              <span className="fl-upload-bar" aria-hidden="true">
                <span style={{ transform: `scaleX(${u.progress})` }} />
              </span>
            </div>
          ))}
        </div>
      )}

      {ask?.kind === 'mkdir' && (
        <NameDialog
          title="New folder"
          action="Create"
          initial="untitled folder"
          onClose={() => setAsk(null)}
          onSave={(name) =>
            fs.mkdir(folder, name).then((it) => {
              setItems((its) => fs.sortItems([...(its ?? []), it]));
              flash(it.id);
            })
          }
        />
      )}
      {ask?.kind === 'rename' && (
        <NameDialog
          title={`Rename ${ask.item.kind === 'dir' ? 'folder' : 'file'}`}
          action="Rename"
          initial={ask.item.name}
          onClose={() => setAsk(null)}
          onSave={(name) =>
            fs.move(ask.item.id, ask.item.parent, name).then((it) => {
              setItems((its) => (its ? fs.sortItems(its.map((x) => (x.id === it.id ? it : x))) : its));
              setRecent((r) => r.map((x) => (x.id === it.id ? { ...it, path: x.path && `${x.path.slice(0, x.path.length - x.name.length)}${it.name}` } : x)));
              setOpen((o) => (o?.id === it.id ? { ...o, ...it } : o));
              flash(it.id);
            })
          }
        />
      )}
      {ask?.kind === 'trash' && (
        <TrashDialog
          item={ask.item}
          machine={machine.id}
          onClose={() => setAsk(null)}
          onTrash={() =>
            fs.trash(ask.item.id).then(() => {
              setItems((its) => its?.filter((x) => x.id !== ask.item.id) ?? its);
              setRecent((r) => r.filter((x) => x.id !== ask.item.id));
              setOpen((o) => (o?.id === ask.item.id ? null : o));
            }, fail(`Couldn’t delete ${ask.item.name}`))
          }
        />
      )}
    </div>
  );
}

/** The file beside the list: its name, what it is, and what it holds. */
function PreviewPane({ item, now, menu, onClose, onFolder }: { item: FileItem; now: number; menu: MenuItem[]; onClose: () => void; onFolder?: () => void }) {
  const kind = previewKind(item);
  const [p, setP] = useState<Preview | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!kind) return;
    let url = '';
    let live = true;
    loadPreview(item, kind).then(
      (r) => {
        if ('url' in r) url = r.url;
        if (live) setP(r);
        else if (url) URL.revokeObjectURL(url);
      },
      (e) => live && setErr(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item, kind]);

  const why: ReactNode = !kind ? 'This kind of file doesn’t preview here.' : p?.kind === 'binary' ? 'It isn’t text, so it doesn’t preview here.' : p?.kind === 'large' ? 'It’s too big to preview here.' : err;

  return (
    <aside className="fl-preview" aria-label={`Preview of ${item.name}`} data-testid="files-preview">
      <div className="fl-preview-head">
        <FileMark item={item} />
        <div className="fl-preview-id">
          <span className="fl-preview-name" title={item.name}>
            {item.name}
          </span>
          <Meta size={11}>
            {fmtBytes(item.size)} · {modified(item.mtime, now)}
            {onFolder && (
              <>
                {' · '}
                <button type="button" className="fl-link" onClick={onFolder}>
                  {folderOf(item.path)}
                </button>
              </>
            )}
          </Meta>
        </div>
        <Tool icon={IconDownload} label="Download" onClick={() => fs.download(item)} data-testid="files-preview-download" />
        <FileMenu name={item.name} items={menu.filter((m) => m.key !== 'dl')} />
        <Tool icon={IconX} label="Close" sub="Esc" align="end" onClick={onClose} data-testid="files-preview-close" />
      </div>
      <div className="fl-preview-body" data-kind={p?.kind ?? kind}>
        {why ? (
          <Empty art="tray" size="sm" title={why} lead="Download it to open it on this computer." data-testid="files-preview-none">
            <Button size="sm" onClick={() => fs.download(item)}>
              <Ic icon={IconDownload} size={14} />
              Download
            </Button>
          </Empty>
        ) : !p ? (
          <div className="fl-loading" aria-label="Loading" />
        ) : p.kind === 'text' ? (
          <pre className="fl-text" data-testid="files-preview-text">
            {p.text}
            {p.cut && <span className="fl-cut">{'\n'}… the rest is in the download</span>}
          </pre>
        ) : p.kind === 'csv' ? (
          <CsvTable rows={p.rows} cut={p.cut} />
        ) : p.kind === 'image' ? (
          <div className="fl-image">
            <img src={p.url} alt={item.name} data-testid="files-preview-image" />
          </div>
        ) : p.kind === 'pdf' ? (
          <iframe className="fl-pdf" src={p.url} title={item.name} data-testid="files-preview-pdf" />
        ) : null}
      </div>
    </aside>
  );
}

/** A file's ⋯: the workspace's menu (a session's ⋯), Delete apart at the foot in the fault colour. */
function FileMenu({ name, items, testid }: { name: string; items: MenuItem[]; testid?: string }) {
  const m = useMenu('.fl-main, .fl-preview', 36 * items.length);
  return (
    <span className="ws-menu-wrap fl-menu" ref={m.ref} data-open={m.open ? '' : undefined}>
      <Tool icon={IconDots} label="More" align="end" aria-haspopup="menu" aria-expanded={m.open} aria-label={`${name} actions`} data-testid={testid} onClick={() => m.setOpen((o) => !o)} />
      {m.p.mounted && (
        <div className="ws-menu ds-float fl-menu-pop" role="menu" aria-label={`Actions for ${name}`} data-flip={m.flip ? '' : undefined} {...presence(m.p)} onKeyDown={menuKeys}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              className="ws-menu-item"
              role="menuitem"
              data-danger={it.danger ? '' : undefined}
              data-testid={it.testid}
              onClick={() => {
                m.setOpen(false);
                it.onSelect();
              }}
            >
              <span className="ws-menu-icon">
                <Ic icon={it.icon} size={16} />
              </span>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/** The title path (the Mac's FilesCrumbs): Files › Projects › app at 20/28, the folder you're in 600 ink, the ones
 *  above 500 muted buttons with a well on hover; a long path keeps home and the last two, the rest behind ⋯. */
function TitleCrumbs({ crumbs, go }: { crumbs: FileItem[]; go: (id: string) => void }) {
  const all = crumbs.length ? crumbs : [{ id: fs.HOME, name: '' } as FileItem];
  const tail = 2;
  const fold = all.length > tail + 2 ? all.slice(1, all.length - tail) : [];
  const steps: (FileItem | 'fold')[] = fold.length ? [all[0], 'fold', ...all.slice(all.length - tail)] : all;
  return (
    <nav className="fl-title" aria-label="Path" data-testid="files-crumbs">
      {steps.map((c, i) => (
        <Fragment key={c === 'fold' ? 'fold' : c.id || 'home'}>
          {i > 0 && <Ic icon={IconChevronRight} size={16} />}
          {c === 'fold' ? (
            <HeadMenu
              label={`${fold.length} more folder${fold.length === 1 ? '' : 's'}`}
              testid="files-crumbs-fold"
              bare
              trigger={<Ic icon={IconDots} size={16} />}
              items={fold.map((f) => ({ key: f.id, label: f.name, icon: IconFolder, testid: 'files-crumb', onSelect: () => go(f.id) }))}
            />
          ) : i === steps.length - 1 ? (
            <h1 className="fl-crumb" aria-current="page">
              {c.id === fs.HOME || !c.name ? 'Files' : c.name}
            </h1>
          ) : (
            <Tooltip label={`Go to ${c.id === fs.HOME || !c.name ? 'Files' : c.name}`}>
              <button type="button" className="fl-crumb" onClick={() => go(c.id)} data-testid={c.id === fs.HOME || i === 0 ? 'files-home' : 'files-crumb'}>
                {c.id === fs.HOME || !c.name ? 'Files' : c.name}
              </button>
            </Tooltip>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

/** A secondary button in the head that opens a menu: + New (New folder, File upload, Folder upload, the Mac's and
 *  Drive's), the folder's ⋯ (a square one), and the folders folded out of a long title path (a bare one). */
function HeadMenu({ items, label, testid, trigger, square, bare }: { items: MenuItem[]; label: string; testid: string; trigger: ReactNode; square?: boolean; bare?: boolean }) {
  const m = useMenu('.fl-main', 36 * items.length + 16);
  return (
    <span className="ws-menu-wrap fl-menu fl-new" ref={m.ref} data-open={m.open ? '' : undefined}>
      <Button
        size="sm"
        variant={bare ? 'icon' : undefined}
        className={square || bare ? 'fl-square' : undefined}
        aria-label={square || bare ? label : undefined}
        aria-haspopup="menu"
        aria-expanded={m.open}
        onClick={() => m.setOpen((o) => !o)}
        data-testid={testid}
      >
        {trigger}
      </Button>
      {m.p.mounted && (
        <div className="ws-menu ds-float fl-menu-pop" role="menu" aria-label={label} data-flip={m.flip ? '' : undefined} {...presence(m.p)} onKeyDown={menuKeys}>
          {items.map((it) => (
            <Fragment key={it.key}>
              {it.line && <div className="ws-menu-rule" role="separator" />}
              <button
                type="button"
                className="ws-menu-item"
                role="menuitem"
                data-testid={it.testid}
                onClick={() => {
                  m.setOpen(false);
                  it.onSelect();
                }}
              >
                <span className="ws-menu-icon">
                  <Ic icon={it.icon} size={16} />
                </span>
                {it.label}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </span>
  );
}

function CsvTable({ rows, cut }: { rows: string[][]; cut: boolean }) {
  const [head, ...body] = rows;
  const cols = useMemo(() => Math.max(0, ...rows.map((r) => r.length)), [rows]);
  if (!head) return null;
  const pad = (r: string[]) => Array.from({ length: cols }, (_, i) => r[i] ?? '');
  return (
    <div className="fl-csv" data-testid="files-preview-csv">
      <table>
        <thead>
          <tr>
            {pad(head).map((c, i) => (
              <th key={i}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {pad(r).map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(cut || rows.length >= 1000) && <p className="fl-cut">The first {rows.length.toLocaleString()} rows; the rest is in the download.</p>}
    </div>
  );
}

/** New folder and Rename: one field, its name selected (without the extension, as Finder does). */
function NameDialog({ title, action, initial, onSave, onClose }: { title: string; action: string; initial: string; onSave: (name: string) => Promise<unknown>; onClose: () => void }) {
  const [draft, setDraft] = useState(initial);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const dot = initial.lastIndexOf('.');
    input.current?.setSelectionRange(0, dot > 0 ? dot : initial.length);
  }, [initial]);
  const next = draft.trim();
  const bad = next.includes('/') ? 'A name can’t have a / in it.' : next === '.' || next === '..' ? 'Pick another name.' : '';
  return createPortal(
    <Dialog title={title} onClose={onClose} testId="files-name-dialog">
      <form
        className="dlg-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!next || bad || busy) return;
          if (next === initial) return onClose();
          setBusy(true);
          onSave(next).then(onClose, (e) => {
            setBusy(false);
            setErr(/409|exists|taken/i.test(String(e)) ? `There’s already something called “${next}” here.` : e instanceof Error ? e.message : String(e));
          });
        }}
      >
        <input
          ref={input}
          className="input"
          value={draft}
          autoComplete="off"
          spellCheck={false}
          aria-label="Name"
          aria-invalid={!!(err || bad) || undefined}
          data-testid="files-name-input"
          onChange={(e) => {
            setDraft(e.target.value);
            setErr('');
          }}
        />
        {(err || bad) && (
          <p className="form-error" role="alert">
            {err || bad}
          </p>
        )}
        <div className="dlg-foot">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!next || !!bad || busy} data-testid="files-name-save">
            {action}
          </Button>
        </div>
      </form>
    </Dialog>,
    document.body,
  );
}

function TrashDialog({ item, machine, onTrash, onClose }: { item: FileItem; machine: string; onTrash: () => Promise<unknown>; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return createPortal(
    <Dialog title={`Delete “${item.name}”?`} onClose={onClose} testId="files-trash-dialog">
      <p>{item.kind === 'dir' ? 'The folder and everything in it go' : 'It goes'} to {machine}’s Trash, so you can still get it back from its desktop.</p>
      <div className="dlg-foot">
        <Button onClick={onClose} autoFocus>
          Cancel
        </Button>
        <Button
          variant="primary"
          tone="danger"
          disabled={busy}
          data-testid="files-trash-confirm"
          onClick={() => {
            setBusy(true);
            void onTrash().finally(onClose);
          }}
        >
          Delete
        </Button>
      </div>
    </Dialog>,
    document.body,
  );
}
